import { useAssetOverrides } from '@/hooks/useAssetOverrides';
import { useState } from 'react';
import ScrollReveal from '@/components/ScrollReveal';
import Lightbox from '@/components/Lightbox';
import ExtraPhotos from '@/components/ExtraPhotos';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Bed, Wifi, Wind, Tv, WashingMachine, Waves, Eye, Star, Droplets } from 'lucide-react';
import zeus1 from '@/assets/zeus-1.jpg';
import zeus2 from '@/assets/zeus-2.jpg';
import zeus3 from '@/assets/zeus-3.jpg';
import zeus4 from '@/assets/zeus-4.jpg';
import zeus5 from '@/assets/zeus-5.jpg';
import zeus6 from '@/assets/zeus-6.jpg';
import zeus7 from '@/assets/zeus-7.jpg';
import zeus8 from '@/assets/zeus-8.jpg';
import zeus9 from '@/assets/zeus-9.jpg';
import zeus10 from '@/assets/zeus-10.jpg';
import zeusBathroom from '@/assets/original-zeus-bathroom.jpg';
import zeusHero from '@/assets/zeus-hero.jpg';

const allImages = [zeus1, zeus2, zeus3, zeus4, zeus5, zeus6, zeus7, zeus8, zeus9, zeus10, zeusBathroom, zeusHero];

export default function ZeusApartment() {
  const { img, extras, sectionExtras } = useAssetOverrides();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const { t } = useLanguage();
  const extraPhotos = extras('zeus');
  const sx = (section: string) => sectionExtras('zeus', section);
  const GALLERY_SECTIONS = ['Courtyard & Pool', 'Master Bedroom', 'Twin Bedroom', 'Bathroom'];
  const allExtras = [...extraPhotos, ...GALLERY_SECTIONS.flatMap(sx)];
  const lightboxImages = allImages.map(img).concat(allExtras.map(p => p.url));
  const openLightbox = (src: string) => setLightboxIndex(allImages.indexOf(src));
  const openUrl = (url: string) => setLightboxIndex(lightboxImages.indexOf(url));


  const highlights = [
    { icon: Bed, label: t('twoBedrooms') }, { icon: Droplets, label: t('privatePoolLabel') },
    { icon: Eye, label: t('panoramicSeaViews') }, { icon: Waves, label: t('stoneVeranda') },
    { icon: Wind, label: t('airConditioning') }, { icon: Wifi, label: t('highSpeedWiFi') },
    { icon: Tv, label: t('smartTV') }, { icon: WashingMachine, label: t('washingMachine') },
  ];

  const amenityList = [
    t('privatePoolAmenity'), t('panoramicSeaViewsAmenity'), t('premiumBedLinens'), t('spaciousCourtyard'),
    t('airConditioningAmenity'), t('highSpeedWiFiAmenity'), t('smartTVAmenity'), t('washingMachineAmenity'),
    t('hairDryer'), t('ironIroningBoard'), t('toiletries'), t('beachTowels'),
    t('stoneBuiltVeranda'), t('woodenFurnishings'), t('bougainvilleaPergola'), t('sunLoungers'),
  ];

  return (
    <div className="bg-background">
      <section className="relative h-screen flex items-end overflow-hidden">
        <img src={img(zeus1)} alt="Zeus Apartment — Sivota Royal" className="absolute inset-0 w-full h-full object-cover hero-image-bright" width={1920} height={1080} fetchPriority="high" style={{ animation: 'heroZoom 25s ease-out forwards' }} />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative z-10 section-padding pb-16 md:pb-24 w-full max-w-3xl">
          <p className="text-white/80 tracking-[0.4em] uppercase text-xs font-sans mb-4 drop-shadow-md">{t('theApartments')}</p>
          <h1 className="font-serif text-6xl md:text-8xl font-light text-white mb-4 drop-shadow-lg">Zeus</h1>
          <p className="text-white/80 text-lg md:text-xl max-w-xl font-light leading-relaxed drop-shadow-md">{t('zeusHeroDesc')}</p>
          <div className="flex gap-3 mt-8">
            <Link to="/booking" className="bg-accent text-accent-foreground px-8 py-4 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-colors">{t('checkAvailability')}</Link>
            <Link to="/booking" className="border border-white/40 text-white px-8 py-4 text-sm tracking-[0.15em] uppercase hover:bg-white/10 transition-colors">{t('sendEnquiry')}</Link>
          </div>
        </div>
      </section>

      <section className="bg-secondary border-b border-border">
        <div className="section-padding py-6">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
            {highlights.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-sm text-muted-foreground"><Icon size={16} className="text-golden" /><span>{label}</span></div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding py-24 lg:py-36">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <ScrollReveal>
            <p className="text-luxury mb-6">{t('overview')}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light mb-8">{t('powerRefinement')}</h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-6">{t('zeusOverviewDesc1')}</p>
            <p className="text-muted-foreground leading-relaxed mb-8">{t('zeusOverviewDesc2')}</p>
            <div className="flex items-center gap-1 text-golden">
              {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="currentColor" />)}
              <span className="text-muted-foreground text-sm ml-2">{t('exceptionalOnBooking')}</span>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <div className="image-zoom aspect-[4/5] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(zeus1)}>
              <img src={img(zeus1)} alt="Zeus courtyard pool" className="w-full h-full object-cover hero-image-bright" loading="lazy" />
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="bg-secondary"><div className="section-padding py-24 lg:py-32"><div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="text-luxury mb-4">{t('courtyardPool')}</p>
          <h3 className="font-serif text-3xl md:text-4xl font-light mb-4">{t('zeusCourtTitle')}</h3>
          <p className="text-muted-foreground leading-relaxed max-w-3xl mb-12">{t('zeusCourtDesc')}</p>
        </ScrollReveal>
        <ScrollReveal delay={0.15}><div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 image-zoom aspect-[21/9] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(zeus1)}><img src={img(zeus1)} alt="Courtyard pool panoramic" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <div className="col-span-3 image-zoom aspect-[3/4] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(zeus2)}><img src={img(zeus2)} alt="Pool detail" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <div className="col-span-3 image-zoom aspect-[3/4] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(zeus3)}><img src={img(zeus3)} alt="Bougainvillea" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <div className="col-span-3 image-zoom aspect-[3/4] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(zeus4)}><img src={img(zeus4)} alt="Stone veranda" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <div className="col-span-3 image-zoom aspect-[3/4] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(zeus5)}><img src={img(zeus5)} alt="Sun loungers" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <ExtraPhotos photos={sx('Courtyard & Pool')} onOpen={openUrl} className="col-span-3 aspect-[3/4]" />
        </div></ScrollReveal>
      </div></div></section>

      <section className="section-padding py-24 lg:py-32"><div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="text-luxury mb-4">{t('masterBedroom')}</p>
          <h3 className="font-serif text-3xl md:text-4xl font-light mb-4">{t('zeusMasterTitle')}</h3>
          <p className="text-muted-foreground leading-relaxed max-w-3xl mb-12">{t('zeusMasterDesc')}</p>
        </ScrollReveal>
        <ScrollReveal delay={0.15}><div className="grid grid-cols-12 gap-3">
          <div className="col-span-7 image-zoom aspect-[16/10] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(zeus6)}><img src={img(zeus6)} alt="Master bedroom" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <div className="col-span-5 flex flex-col gap-3">
            <div className="flex-1 image-zoom cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(zeus7)}><img src={img(zeus7)} alt="Wooden furnishings" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
            <div className="flex-1 image-zoom cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(zeus8)}><img src={img(zeus8)} alt="Bedroom veranda" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          </div>
          <ExtraPhotos photos={sx('Master Bedroom')} onOpen={openUrl} />
        </div></ScrollReveal>
      </div></section>

      <section className="bg-secondary"><div className="section-padding py-24 lg:py-32"><div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="text-luxury mb-4">{t('twinBedroom')}</p>
          <h3 className="font-serif text-3xl md:text-4xl font-light mb-4">{t('zeusTwinTitle')}</h3>
          <p className="text-muted-foreground leading-relaxed max-w-3xl mb-12">{t('zeusTwinDesc')}</p>
        </ScrollReveal>
        <ScrollReveal delay={0.15}><div className="grid grid-cols-12 gap-3">
          <div className="col-span-8 image-zoom aspect-[16/9] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(zeus9)}><img src={img(zeus9)} alt="Twin bedroom" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <div className="col-span-4 image-zoom aspect-[16/9] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(zeus10)}><img src={img(zeus10)} alt="Balcony view" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
          <ExtraPhotos photos={sx('Twin Bedroom')} onOpen={openUrl} />
        </div></ScrollReveal>
      </div></div></section>

      {(
        <section className="section-padding py-24 lg:py-32"><div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <p className="text-luxury mb-4">{t('bathroom')}</p>
            <h3 className="font-serif text-3xl md:text-4xl font-light mb-4">{t('zeusBathTitle')}</h3>
            <p className="text-muted-foreground leading-relaxed max-w-3xl mb-12">{t('zeusBathDesc')}</p>
          </ScrollReveal>
          <ScrollReveal delay={0.15}><div className="grid grid-cols-12 gap-3">
            <div className="col-span-6 image-zoom aspect-[4/3] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(zeusBathroom)}><img src={img(zeusBathroom)} alt="Zeus bathroom" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
            <ExtraPhotos photos={sx('Bathroom')} onOpen={openUrl} className="col-span-6 aspect-[4/3]" />
          </div></ScrollReveal>
        </div></section>
      )}

      <section className="section-padding py-24 lg:py-32"><div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="text-luxury mb-4">{t('stoneVerandaLabel')}</p>
          <h3 className="font-serif text-3xl md:text-4xl font-light mb-4">{t('zeusVerandaTitle')}</h3>
          <p className="text-muted-foreground leading-relaxed max-w-3xl mb-12">{t('zeusVerandaDesc')}</p>
        </ScrollReveal>
        <ScrollReveal delay={0.15}><div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 image-zoom aspect-[21/9] cursor-pointer rounded-sm overflow-hidden" onClick={() => openLightbox(zeusHero)}><img src={img(zeusHero)} alt="Zeus panoramic veranda" className="w-full h-full object-cover hero-image-bright" loading="lazy" /></div>
        </div></ScrollReveal>
      </div></section>

      <section className="bg-secondary"><div className="section-padding py-20"><div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <p className="text-luxury mb-6">{t('amenities')}</p>
          <h3 className="font-serif text-3xl font-light mb-10">{t('everythingYouNeed')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {amenityList.map(a => (
              <div key={a} className="bg-background px-5 py-4 text-sm text-foreground border border-border/60 rounded-sm">{a}</div>
            ))}
          </div>
        </ScrollReveal>
      </div></div></section>

      <section className="relative py-28 lg:py-36">
        <img src={img(zeus1)} alt="" className="absolute inset-0 w-full h-full object-cover hero-image-bright" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/10" />
        <div className="relative z-10 text-center section-padding">
          <ScrollReveal>
            <p className="text-white/60 tracking-[0.3em] uppercase text-xs mb-4">{t('bookYourStay')}</p>
            <h3 className="font-serif text-4xl md:text-5xl font-light text-white mb-4">{t('readyExperience')} Zeus?</h3>
            <p className="text-white/70 max-w-lg mx-auto mb-10">{t('secureYourDatesZeus')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking" className="bg-accent text-accent-foreground px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-colors">{t('checkAvailability')}</Link>
              <Link to="/booking" className="border border-white/40 text-white px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-white/10 transition-colors">{t('sendEnquiry')}</Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {extraPhotos.length > 0 && (
        <section className="section-padding py-16">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-7xl mx-auto">
            {extraPhotos.map((p, i) => (
              <div
                key={p.id}
                className="image-zoom aspect-[4/3] cursor-pointer rounded-sm overflow-hidden"
                onClick={() => setLightboxIndex(allImages.length + i)}
              >
                <img src={p.url} alt={p.alt_text || 'Sivota Royal photo'} className="w-full h-full object-cover hero-image-bright" loading="lazy" />
              </div>
            ))}
          </div>
        </section>
      )}

      {lightboxIndex !== null && <Lightbox images={lightboxImages} initialIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />}
    </div>
  );
}
